import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useDispatch } from 'react-redux';
import { notificationApi } from '@/state/notifications/notification-api';
import { toast } from 'sonner';
import { parseNotificationMsg } from '@/lib/notification-utils';
import Session from 'supertokens-auth-react/recipe/session';

// Use same-origin proxy
const SOCKET_URL = '';

export const useSocketNotifications = (userId?: string) => {
  const dispatch = useDispatch();
  const socketRef = useRef<Socket | null>(null);
  const [sessionValid, setSessionValid] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const exists = await Session.doesSessionExist();
        if (exists) {
          // Attempt to refresh session to get latest token
          await Session.attemptRefreshingSession();
          setSessionValid(true);
        } else {
          setSessionValid(false);
        }
      } catch (err) {
        console.error('Error checking/refreshing session:', err);
        setSessionValid(false);
      }
    };

    if (userId) {
      checkSession();
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !sessionValid) return;

    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL, {
        withCredentials: true,
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
    }

    const socket = socketRef.current;

    const onConnect = () => {
      socket.emit('register', userId);
    };

    const onNotifications = (message: any) => {

      try {
        const msgStr = typeof message === 'string' ? message : JSON.stringify(message);
        const payload = parseNotificationMsg(msgStr);

        if (payload.text) {
          toast.info(payload.text, {
            description: payload.notification_type || 'New Notification',
            duration: 5000,
          });
        }
      } catch (e) {
        toast.info('New notification received');
      }

      dispatch(notificationApi.util.invalidateTags(['Notifications']));
    };

    const onError = (err: any) => {
      console.error('Socket error:', err);
    };

    socket.on('connect', onConnect);
    socket.on('notifications', onNotifications);
    socket.on('connect_error', onError);
    socket.on('error', onError);

    if (socket.connected) {
      onConnect();
    }

    socket.on('disconnect', (reason) => {
      console.log('Disconnected from push server:', reason);
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('notifications', onNotifications);
      socket.off('connect_error', onError);
      socket.off('error', onError);
      socket.off('disconnect');
    };
  }, [userId, sessionValid, dispatch]);

  return socketRef.current;
};
